// Feature flags for the partners portal. Currency and warehouse UI are hidden
// for now. Kept as flags (not deletions) so fields/columns can be restored
// without code changes by flipping these to `true`.
export const SHOW_CURRENCY_FIELD = false;
export const SHOW_WAREHOUSE_FIELDS = false;

// Shared palette for the Inventory Management section (mirrors the source app).
export const colors = {
  primary: '#ff6900',
  primaryDark: '#d34801',
  primaryLight: '#fff7f1',
  primaryTint: '#fff7f1',
  accent: '#02ab6c',
  navy: '#011835',
  white: '#ffffff',
  bg: '#f9f9f9',
  gray100: '#f3f4f5',
  gray200: '#e8e8e8',
  gray300: '#cfd3d9',
  gray400: '#9a9ea6',
  gray500: '#71717a',
  gray600: '#4f5969',
  gray700: '#475467',
  border: '#e8e8e8',
  error: '#dc3545',
  warning: '#ff8900',
  info: '#0693e3',
  success: '#02ab6c',
};

// Maps a semantic status to a MUI Chip color + label styling.
export const STATUS_COLORS = {
  // generic
  active: 'success',
  inactive: 'default',
  // invoices
  draft: 'default',
  submitted: 'info',
  partially_received: 'warning',
  received: 'success',
  cancelled: 'error',
  // payment
  unpaid: 'error',
  partial: 'warning',
  paid: 'success',
  // lots
  available: 'success',
  reserved: 'info',
  consumed: 'default',
  expired: 'error',
  quarantined: 'warning',
  // transfers
  pending: 'warning',
  in_transit: 'info',
  // reservations
  fulfilled: 'success',
  released: 'default',
  // counts
  planned: 'info',
  in_progress: 'warning',
  completed: 'success',
  // imports ("partial" and "completed" are shared with the entries above)
  failed: 'error',
  // users
  approved: 'success',
  rejected: 'error',
  suspended: 'error',
  // alerts
  info: 'info',
  critical: 'error',
};

// What happened to a single line of an import run.
export const IMPORT_ACTION_CHIP = {
  created: { label: 'Created', color: 'success' },
  updated: { label: 'Updated', color: 'info' },
  failed: { label: 'Failed', color: 'error' },
};

export const VENDOR_TYPES = ['supplier', 'manufacturer', 'distributor'];
export const PAYMENT_TERMS = ['net_30', 'net_60', 'cod', 'prepaid'];
export const WAREHOUSE_TYPES = ['warehouse', 'store', 'distribution_center'];
export const LOCATION_TYPES = [
  'storage',
  'picking',
  'receiving',
  'shipping',
  'quarantine',
];
export const ADJUSTMENT_REASONS = [
  'damage',
  'theft',
  'expired',
  'count_correction',
  'return',
  'waste',
  'other',
];
export const COUNT_TYPES = ['full', 'cycle', 'spot'];
export const COSTING_METHODS = ['fifo', 'lifo', 'fefo', 'weighted_avg'];
export const OVERSELL_BEHAVIORS = ['block', 'backorder', 'allow'];
export const MEMBERSHIP_ROLES = ['owner', 'admin', 'manager', 'staff'];
export const USER_STATUSES = ['pending', 'approved', 'rejected', 'suspended'];

/** Humanizes an enum-like token, e.g. "partially_received" -> "Partially Received". */
export const humanize = (value) =>
  String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const formatCurrency = (value, currency = 'USD') => {
  const amount = Number(value || 0);
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
};

export const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString();
};

/** Date + time, for records where several can land on the same day. */
export const formatDateTime = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};
